import { useEffect, useMemo, useReducer, useCallback } from 'react';
import { useCalibrationHistory } from 'talent_matching/api';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import {
  CalibrationHistoryEntry,
  CalibrationSettings,
  SettingsFormData,
} from 'talent_matching/types';
import { useUserContext } from 'talent_matching/contexts/user_context';

type Params = {
  paths: ReturnType<typeof useUserContext>['paths'];
  isOpen: boolean;
  isHistoryTab: boolean;
  buildFormValuesFromCalibration: (
    calibration: Partial<CalibrationSettings>
  ) => Partial<SettingsFormData>;
  reset: (values: Partial<SettingsFormData>) => void;
  setActiveTab: (tab: 'current' | 'history') => void;
  applyingHistoryRef: React.MutableRefObject<boolean>;
};

export const useCalibrationHistoryState = ({
  paths,
  isOpen,
  isHistoryTab,
  buildFormValuesFromCalibration,
  reset,
  setActiveTab,
  applyingHistoryRef,
}: Params) => {
  const {
    data: calibrationHistory,
    isLoading: isHistoryLoading,
    error: historyError,
  } = useCalibrationHistory(paths, isOpen && isHistoryTab);

  type State = {
    selectedHistoryId: number | null;
  };

  type Action =
    | { type: 'reset' }
    | { type: 'setSelectedHistoryId'; id: number | null };

  const reducer = (state: State, action: Action): State => {
    switch (action.type) {
      case 'reset':
        // Return same reference if already reset to avoid unnecessary re-renders
        if (state.selectedHistoryId === null) {
          return state;
        }
        return {
          selectedHistoryId: null,
        };
      case 'setSelectedHistoryId':
        return { ...state, selectedHistoryId: action.id };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, {
    selectedHistoryId: null,
  });
  const [isUseVersionModalOpen, openUseVersionModal, closeUseVersionModal] =
    useModal();

  const historyItems = useMemo(
    () => calibrationHistory?.history || [],
    [calibrationHistory]
  );

  const selectedHistory = useMemo(
    () =>
      historyItems.find((entry) => entry.id === state.selectedHistoryId) ||
      historyItems[0],
    [historyItems, state.selectedHistoryId]
  );

  useEffect(() => {
    if (!isOpen) {
      setActiveTab('current');
      dispatch({ type: 'reset' });
      closeUseVersionModal();
    }
  }, [closeUseVersionModal, isOpen, setActiveTab]);

  useEffect(() => {
    if (state.selectedHistoryId === null && historyItems.length > 0) {
      dispatch({ type: 'setSelectedHistoryId', id: historyItems[0].id });
    }
  }, [historyItems, state.selectedHistoryId]);

  const setSelectedHistoryId = useCallback(
    (id: number | null) => dispatch({ type: 'setSelectedHistoryId', id }),
    [dispatch]
  );

  const handleUseVersionClick = useCallback(
    (entry: CalibrationHistoryEntry) => {
      dispatch({ type: 'setSelectedHistoryId', id: entry.id });
      openUseVersionModal();
    },
    [openUseVersionModal]
  );

  const dismissUseVersionModal = useCallback(() => {
    closeUseVersionModal();
  }, [closeUseVersionModal]);

  const applyHistoryEntryToCurrent = useCallback(() => {
    if (!selectedHistory) {
      return;
    }

    const values = buildFormValuesFromCalibration(selectedHistory);
    reset(values);
    applyingHistoryRef.current = true;
    setActiveTab('current');
    closeUseVersionModal();
  }, [
    buildFormValuesFromCalibration,
    closeUseVersionModal,
    reset,
    selectedHistory,
    setActiveTab,
    applyingHistoryRef,
  ]);

  return {
    historyItems,
    isHistoryLoading,
    historyError,
    selectedHistory,
    selectedHistoryId: state.selectedHistoryId,
    setSelectedHistoryId,
    isUseVersionModalOpen,
    handleUseVersionClick,
    dismissUseVersionModal,
    applyHistoryEntryToCurrent,
  };
};
